// Playing a listener's own tape: songs from any day of either stream, each with its own day's
// sounds and its stream's tempo (125 or 72 BPM). Every song
// plays once in the engine's "track" mode (the song, then its day's break, at its loudness gain),
// then the transport stops by itself and the next track starts — its kit and notes are loaded
// while the previous one plays, so the hand-over is quick.
import { create } from "zustand";
import {
  clockFor,
  mixSeed,
  SONG_TICKS,
  TICKS_PER_BAR,
  hashString,
  type Bpm,
  type LoopData,
  type PeriodSpec,
} from "@eb/shared";
import type { EngineHost } from "../audio/host";
import { stopMixtape } from "../countdown/mixtape";
import { riverPlayer, stopPreview, stopRiverIfPlaying } from "../river/context";

export interface TapeTrack {
  loopId: string;
  title: string | null;
  author: string | null;
  /** The song's stream (missing: the main one). */
  bpm?: Bpm;
  day: number;
  gain: number;
}

interface TapePlayerState {
  tapeId: string | null;
  playing: boolean;
  starting: boolean;
  index: number;
  /** Share of the current track played (0–1). */
  progress: number;
  error: string;
}

export const useTapePlayer = create<TapePlayerState>(() => ({
  tapeId: null,
  playing: false,
  starting: false,
  index: 0,
  progress: 0,
  error: "",
}));

const TRACK_TICKS = SONG_TICKS + TICKS_PER_BAR;
let host: EngineHost | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let session = 0;
let seenPlaying = false;
const ready = new Map<number, Promise<{ spec: PeriodSpec; data: LoopData } | null>>();

function prepare(tracks: TapeTrack[], i: number, h: EngineHost) {
  if (i < 0 || i >= tracks.length) return Promise.resolve(null);
  let p = ready.get(i);
  if (!p) {
    p = (async () => {
      const player = await riverPlayer();
      const t = tracks[i];
      const [spec, data] = await Promise.all([player.spec(t.day, h, t.bpm ?? 125), player.loop(t.loopId)]);
      return data ? { spec, data } : null;
    })().catch(() => null);
    ready.set(i, p);
  }
  return p;
}

async function startTrack(tracks: TapeTrack[], i: number, mine: number): Promise<void> {
  const h = host;
  if (!h || mine !== session) return;
  if (i >= tracks.length) {
    stopTape();
    return;
  }
  const t = await prepare(tracks, i, h);
  if (mine !== session) return;
  if (!t) {
    // A song that's gone (removed): skip it.
    void startTrack(tracks, i + 1, mine);
    return;
  }
  const slot = mixSeed(hashString(tracks[i].loopId), 3) % clockFor(tracks[i].bpm ?? 125).SLOTS_PER_DAY;
  h.stop();
  h.setEditor(t.spec, t.data, { type: "track", slot, gain: tracks[i].gain });
  h.play();
  seenPlaying = false;
  useTapePlayer.setState({ playing: true, starting: false, index: i, progress: 0 });
  void prepare(tracks, i + 1, h);
}

/** Play `tracks` from `index`. Call `engineHost()` in the click handler, before any await. */
export async function playTape(hostPromise: Promise<EngineHost>, tapeId: string, tracks: TapeTrack[], index = 0) {
  const mine = ++session;
  ready.clear();
  useTapePlayer.setState({ tapeId, starting: true, error: "", index });
  try {
    const h = await hostPromise;
    await h.resume();
    await stopPreview();
    stopMixtape();
    await stopRiverIfPlaying();
    if (mine !== session) return;
    host = h;
    await startTrack(tracks, index, mine);
    if (timer) clearInterval(timer);
    timer = setInterval(() => {
      const c = h.lastClock;
      if (!c || mine !== session || c.world) return;
      if (c.playing) {
        seenPlaying = true;
        const progress = Math.max(0, Math.min(1, c.transportTick / TRACK_TICKS));
        if (Math.abs(progress - useTapePlayer.getState().progress) > 0.005) useTapePlayer.setState({ progress });
      } else if (seenPlaying) {
        // The track (song + break) is over: the next one.
        seenPlaying = false;
        void startTrack(tracks, useTapePlayer.getState().index + 1, mine);
      }
    }, 100);
  } catch (e) {
    useTapePlayer.setState({ starting: false, playing: false, error: e instanceof Error ? e.message : String(e) });
  }
}

export function stopTape(): void {
  session++;
  if (timer) clearInterval(timer);
  timer = null;
  ready.clear();
  host?.stop();
  const s = useTapePlayer.getState();
  if (s.playing || s.starting) useTapePlayer.setState({ playing: false, starting: false, progress: 0 });
}
